if (!customElements.get('edge-forward')) {
  class EdgeForward extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.stage = this.querySelector('.edge-forward__stage');
      this.redScroll = this.querySelector('[data-edge-forward-scroll]');
      this.whiteLine = this.querySelector('[data-edge-forward-white]');
      this.blackLine = this.querySelector('[data-edge-forward-black]');
      this.storySurface = this.querySelector('.edge-forward__story-surface');
      this.section = this.closest('.edge-forward');
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.viewportWidth = window.innerWidth;
      this.titleInRange = false;
      this.requestScrollTriggerRefresh = this.requestScrollTriggerRefresh.bind(this);
      this.onScroll = this.onScroll.bind(this);

      if (!this.stage || !this.redScroll || !this.whiteLine || !this.blackLine || !this.storySurface || !this.section) return;

      this.abortController = new AbortController();
      this.onResize = this.onResize.bind(this);
      window.addEventListener('resize', this.onResize, {
        passive: true,
        signal: this.abortController.signal,
      });
      window.addEventListener('load', this.requestScrollTriggerRefresh, {
        once: true,
        signal: this.abortController.signal,
      });
      window.addEventListener('pageshow', this.requestScrollTriggerRefresh, {
        signal: this.abortController.signal,
      });
      window.addEventListener('scroll', this.requestScrollTriggerRefresh, {
        once: true,
        passive: true,
        signal: this.abortController.signal,
      });
      window.addEventListener('scroll', this.onScroll, {
        passive: true,
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:section:load', this.requestScrollTriggerRefresh, {
        signal: this.abortController.signal,
      });
      document.addEventListener('shopify:section:reorder', this.requestScrollTriggerRefresh, {
        signal: this.abortController.signal,
      });

      const layoutRoot = this.section.closest('main');
      if (layoutRoot && 'ResizeObserver' in window) {
        this.layoutObserver = new ResizeObserver(this.requestScrollTriggerRefresh);
        // Earlier lazy media can move this section without resizing its immediate sibling.
        this.layoutObserver.observe(layoutRoot);
      }

      if ('IntersectionObserver' in window) {
        this.titleObserver = new IntersectionObserver(
          ([entry]) => {
            this.titleInRange = entry.isIntersecting;
            if (this.titleInRange) this.onScroll();
          },
          { rootMargin: '100% 0px' },
        );
        this.titleObserver.observe(this.redScroll);
      } else {
        this.titleInRange = true;
      }

      if (this.reducedMotion) {
        this.setStaticState();
        return;
      }

      this.section.classList.add('edge-forward--enhanced');
      this.waitForGsap();
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      this.abortController?.abort();
      this.abortController = null;
      window.clearTimeout(this.gsapTimer);
      this.gsapTimer = null;
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = null;
      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = null;
      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.layoutObserver?.disconnect();
      this.layoutObserver = null;
      this.titleObserver?.disconnect();
      this.titleObserver = null;
      this.scrollTrigger?.kill();
      this.scrollTrigger = null;
      this.context?.revert();
      this.context = null;
      this.gsap?.set([this.whiteLine, this.blackLine], {
        clearProps: 'opacity,visibility,transform',
      });
      this.gsap = null;
      this.ScrollTrigger = null;
      this.section?.classList.remove('edge-forward--enhanced');
      this.section?.style.removeProperty('--edge-forward-stage-height');
      this.section?.style.removeProperty('--edge-forward-scroll-distance');
    }

    waitForGsap() {
      const gsap = window.gsap;
      const ScrollTrigger = window.ScrollTrigger || gsap?.core?.globals?.().ScrollTrigger;

      if (gsap && ScrollTrigger) {
        gsap.registerPlugin(ScrollTrigger);
        this.createTimeline(gsap, ScrollTrigger);
        return;
      }

      this.gsapTimer = window.setTimeout(() => this.waitForGsap(), 100);
    }

    createTimeline(gsap, ScrollTrigger) {
      this.scrollTrigger?.kill();
      this.scrollTrigger = null;
      this.context?.revert();
      this.gsap = gsap;
      this.ScrollTrigger = ScrollTrigger;

      const desktop = window.innerWidth >= 768;
      const distanceSetting = desktop
        ? this.section.dataset.scrollDistanceDesktop
        : this.section.dataset.scrollDistanceMobile;
      const distance = Number(distanceSetting) || (desktop ? 155 : 130);
      const requestedPinDistance = window.innerHeight * (distance / 100);
      const pinDistance = Math.max(requestedPinDistance, this.stage.offsetHeight);
      // Preserve a short full-screen beat after the title settles without intercepting input.
      const holdDistance = this.stage.offsetHeight * 0.25;
      const totalPinDistance = pinDistance + holdDistance;

      this.pinDistance = pinDistance;
      this.titleProgressScale = totalPinDistance / pinDistance;
      this.updateLineStartOffsets();
      this.section.style.setProperty('--edge-forward-stage-height', `${this.stage.offsetHeight}px`);
      this.section.style.setProperty('--edge-forward-scroll-distance', `${totalPinDistance}px`);

      let timeline;

      this.context = gsap.context(() => {
        timeline = gsap.timeline({
          defaults: { ease: 'none' },
          scrollTrigger: {
            trigger: this.redScroll,
            start: 'top top',
            end: 'bottom bottom',
            scrub: desktop ? 0.45 : 0.3,
            invalidateOnRefresh: true,
            // This section follows other pinned experiences, so their spacing must settle first.
            refreshPriority: -1,
            onRefresh: (trigger) => {
              this.updateLineStartOffsets();
              this.updateTitleState(trigger.progress);
            },
            onUpdate: (trigger) => this.updateTitleState(trigger.progress),
          },
        });

        timeline
          .addLabel('hold', 0.68)
          .addLabel('exit', 1.12)
          .to({}, { duration: 0.28 }, 'hold')
          // Story stays full-size and opaque while it rises in normal document flow.
          .to({}, { duration: 1.08 }, 'exit')
          .to({}, { duration: 0.16 });
      }, this);

      this.scrollTrigger = timeline.scrollTrigger;
      ScrollTrigger.refresh();
      document.fonts?.ready.then(this.requestScrollTriggerRefresh);
    }

    requestScrollTriggerRefresh() {
      if (!this.isConnected || !this.ScrollTrigger) return;

      window.cancelAnimationFrame(this.refreshFrame);
      this.refreshFrame = window.requestAnimationFrame(() => {
        this.refreshFrame = window.requestAnimationFrame(() => {
          this.refreshFrame = null;
          this.ScrollTrigger?.sort();
          this.ScrollTrigger?.refresh();
        });
      });
    }

    onScroll() {
      if (!this.titleInRange || !this.gsap) return;

      window.cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = window.requestAnimationFrame(() => {
        this.scrollFrame = null;
        this.updateTitleState(this.scrollTrigger?.progress || 0);
      });
    }

    updateTitleState(progress) {
      if (!this.gsap) return;

      // Previous pinned sections can offset ScrollTrigger's cached start on long pages.
      const visibleScrollDistance = Math.max(0, -this.redScroll.getBoundingClientRect().top);
      const titleProgress = this.pinDistance
        ? Math.min(1, visibleScrollDistance / this.pinDistance)
        : Math.min(1, progress * this.titleProgressScale);

      this.setLineState(this.whiteLine, titleProgress, 0.025, this.whiteLineStartOffset);
      this.setLineState(this.blackLine, titleProgress, 0.068, this.blackLineStartOffset);
    }

    setLineState(line, progress, start, startOffset) {
      const duration = 0.24;
      const lineProgress = Math.min(1, Math.max(0, (progress - start) / duration));
      const easedProgress = 1 - (1 - lineProgress) ** 2;
      const offset = startOffset * (1 - easedProgress);

      this.gsap.set(line, {
        autoAlpha: easedProgress,
        x: offset,
      });
    }

    updateLineStartOffsets() {
      if (window.innerWidth < 1280) {
        const configuredOffset = Number.parseFloat(
          window.getComputedStyle(this.section).getPropertyValue('--edge-forward-title-start-offset'),
        );
        const offset = Number.isFinite(configuredOffset) ? configuredOffset : 0;

        this.whiteLineStartOffset = -offset;
        this.blackLineStartOffset = offset;
        return;
      }

      const configuredInset = Number.parseFloat(
        window.getComputedStyle(this.section).getPropertyValue('--edge-forward-title-start-inset'),
      );
      const inset = Number.isFinite(configuredInset) ? configuredInset : 0;
      const stageWidth = this.stage.clientWidth;
      const whiteTextWidth = this.getLineTextWidth(this.whiteLine);
      const blackTextWidth = this.getLineTextWidth(this.blackLine);

      this.whiteLineStartOffset = -Math.max(0, (stageWidth - whiteTextWidth) / 2 - inset);
      this.blackLineStartOffset = Math.max(0, (stageWidth - blackTextWidth) / 2 - inset);
    }

    getLineTextWidth(line) {
      const text = line.querySelector('[data-edge-forward-line-text]');

      return Math.min(text?.getBoundingClientRect().width || line.clientWidth, this.stage.clientWidth);
    }

    setStaticState() {
      this.whiteLine.style.opacity = '1';
      this.whiteLine.style.transform = 'none';
      this.blackLine.style.opacity = '1';
      this.blackLine.style.transform = 'none';
    }

    onResize() {
      window.cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = window.requestAnimationFrame(() => {
        if (this.reducedMotion) return;

        const viewportWidth = window.innerWidth;

        // Mobile browser chrome changes height while scrolling; rebuilding then resets the title timeline.
        if (viewportWidth < 768 && viewportWidth === this.viewportWidth) return;

        this.viewportWidth = viewportWidth;

        const gsap = window.gsap;
        const ScrollTrigger = window.ScrollTrigger || gsap?.core?.globals?.().ScrollTrigger;
        if (gsap && ScrollTrigger) this.createTimeline(gsap, ScrollTrigger);
      });
    }
  }

  customElements.define('edge-forward', EdgeForward);
}
